<template>
  <v-form v-model="valid" ref="form">

    <v-container fluid class="mt-4" @click="selectedInputItem = null">
      <v-row>
        <v-col cols="12" md="10" lg="7">
          <outlined-block title="Bill of materials" :rules="[assemblyValid]">
            <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                          :items="item.bom" item-key="key" no-data-text="No items specified"
                          :item-class="item => item.key=== selectedInputItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                          :headers="[{text:'',value:'add',width:'10%',align:'center',cellClass:'pa-0',divider:true},
                                    {text:'Item',value:'item',width:'40%',divider:true,class:'px-2',cellClass:'py-2 px-1'},
                                    {text:'Quantity',value:'quantity',width:'25%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-1'},
                                    {text:'Optional',value:'optional',width:'10%',align:'center',divider:true,class:'px-2',cellClass:'py-2 px-1'}]" class="no-sort" ref="tableBody"
                              @click.native.stop=""
                              @click:row="item => {selectedInputItem = item.key; $nextTick(() => this.$refs.form.validate())}">
              <template v-slot:item.add="{index}">
                <v-icon class="handle mt-n2">mdi-drag</v-icon>
                <v-btn class="mt-n2" icon @click.stop="deleteItem(index)" x-small><v-icon>mdi-delete</v-icon></v-btn>
              </template>
              <template v-slot:item.item="{item}">
                <template v-if="selectedInputItem === item.key">
                  <multi-level-select v-if="categories.length" outlined label="Item"
                                :menuData="eligibleItems" :second.sync="item.id" actionEvent="addItem" actionText="Add new item"
                                @addItem="inputItem=item;itemCreate=true"/>
                  <v-autocomplete v-else dense outlined label="Item" v-model="item.id" :items="items" item-value="id" item-text="name" class="text-body-2" :rules="[fieldRules.required]" no-data-text="No items found">
                    <template v-slot:prepend-item>
                      <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="inputItem=item;itemCreate=true">
                        <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new item
                      </div>
                    </template>
                  </v-autocomplete>
                </template>
                <div v-else :class="{'error--text':!item.id}">{{itemDescription(item)}}</div>
              </template>
              <template v-slot:item.quantity="{item}">
                <decimal-number-input v-if="selectedInputItem === item.key" label="Quantity" v-model="item.quantity" prefix=""
                      :suffix="trackUnits(item.id)" :precision="precision(item.id)" class="text-body-2"/>
                <div v-else :class="{'error--text':item.quantity<0}">{{item.quantity | number(precision(item.id))}} {{trackUnits(item.id)}}</div>
              </template>
              <template v-slot:item.optional="{item}">
                <v-simple-checkbox v-if="selectedInputItem === item.key" class="mt-n1" v-model="item.optional"/>
                <v-icon v-else-if="item.optional">mdi-check</v-icon>
              </template>
              <template v-slot:footer="{widths}">
                <v-divider/>
                <div @click="selectedInputItem = null" style="width:100%" class="py-2 px-0">
                  <div :style="'width:'+widths[0]+'px'" class="text-center">
                    <v-btn fab color="primary" x-small @click.stop="addItem"><v-icon small>mdi-plus</v-icon></v-btn>
                  </div>
                </div>
              </template>
            </v-data-table>
          </outlined-block>
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-col cols="12" md="6">
          <v-textarea label="Production notes" v-model="item.description" outlined dense no-resize rows="4" :rules="[fieldRules.maxLength(1000)]"/>
        </v-col>
      </v-row>
      <v-row v-if="$store.getters.company.allowPlanning">
        <v-col cols="12" md="6">
          <production-planning :planning="item.productionPlanningInfo" :durationPerUnit="item.trackUnits"/>
        </v-col>
      </v-row>
    </v-container>

    <inventory-items-quick-create v-model="itemCreate" @create="itemCreated"/>
  </v-form>
</template>

<script>
import sortableJS from 'sortablejs';
import InventoryItemsQuickCreate from '@/views/inventory/items/InventoryItemsQuickCreate'
import ProductionPlanning from '@/views/production/ProductionPlanning'
import createHierarchyList from '@/mixins/createHierarchyList'

export default {
  name: 'InventoryItemsEditAssembly',
  mixins: [createHierarchyList],
  components: {InventoryItemsQuickCreate,ProductionPlanning},
  props: {
    item: {type:Object, required:true},
  },
  data() {
    return {
      valid: true,
      items: [],
      categories: [],
      itemCreate: false,
      inputItem: null,
      sortable: null,
      selectedInputItem: null
    }
  },
  computed: {
    eligibleItems() {
      const categories = this.createHierarchyList(this.categories);
      for (const category of categories) {
        const sub = this.items.filter(item => item.category?.id === category.id);
        if (sub.length)
          category.menu = sub;
        else
          category.disabled = true;
      }
//      const items = [...categories.filter(category => !category.disabled)];
      const items = categories;
      const leftOver = this.items.filter(item => !item.category?.id);
      if (leftOver.length)
        items.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
      return items;
    },
    assemblyValid() {
      return this.item.bom.every(item => !!item.id && item.quantity >= 0);
    }
  },
  watch: {
    item: {
      handler() {this.$emit('input', true);},
      immediate: true,
      deep: true
    }
  },
  async created() {
    this.$lazyListLoad(this.categories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]);
    this.$lazyListLoad(this.items, 'items2', 'id,name,trackUnits,precision,category{id}', 100, [{field:'active', comparator:'eq', value:'true'}]);
  },
  mounted() {
    const _self = this;
    this.sortable = sortableJS.create(this.$refs.tableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.item.bom.splice(oldIndex, 1)[0];
        _self.item.bom.splice(newIndex, 0, rowSelected);
      }
    })
  },
  beforeDestroy() {
    this.sortable.destroy();
  },
  methods: {
    itemDescription(item) {
      if (!item.id)
        return 'MISSING';
      return `${this.items?.find(i => i.id === item.id)?.name}`;
    },
    async itemCreated(item) {
      this.items = (await this.$store.dispatch('items')).filter(item => item.active);
      if (this.inputItem)
        this.inputItem.id = item.id;
    },
    trackUnits(id) {
      const item = this.items.find(i => i.id === id);
      return item ? item.trackUnits : '';
    },
    precision(id) {
      const item = this.items.find(i => i.id === id);
      return item ? item.precision : 0;
    },
    addItem() {
      const item = {id:null,quantity:0,optional:false,key:Date.now()};
      this.item.bom.push(item);
      this.selectedInputItem = item.key;
      this.$nextTick(() => this.$refs.form.validate());
    },
    deleteItem(index) {
      this.item.bom.splice(index, 1);
    },
  }
}
</script>